import React, { useState, useEffect } from 'react'
import { fetchGalleryBySlug, getResizedImageUrl } from '../lib/cms'
import Lightbox from './Lightbox'

export default function CMSGallery({ gallerySlug, title, subtitle, maxItems }) {
  const [gallery, setGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [activePhotoIdx, setActivePhotoIdx] = useState(0)

  useEffect(() => {
    let isMounted = true
    async function load() {
      if (!gallerySlug) return
      setLoading(true)
      const data = await fetchGalleryBySlug(gallerySlug)
      if (isMounted) {
        setGallery(data)
        setLoading(false)
      }
    }
    load()
    return () => { isMounted = false }
  }, [gallerySlug])

  if (loading) {
    return (
      <div className="w-full py-8 text-center text-slate-400">
        <div className="inline-flex items-center gap-2 text-sm">
          <i className="fa-solid fa-spinner fa-spin text-sky-500" aria-hidden="true" />
          <span>Indlæser galleri...</span>
        </div>
      </div>
    )
  }

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null
  }

  const displayedImages = maxItems ? gallery.images.slice(0, maxItems) : gallery.images

  const openLightbox = (index) => {
    setActivePhotoIdx(index)
    setLightboxOpen(true)
  }

  return (
    <section className="w-full">
      {(title || subtitle) && (
        <div className="mb-6 lg:mb-8 text-center lg:text-left">
          {title && (
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="mt-1 text-sm sm:text-base text-slate-600">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* Grid of gallery images */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {displayedImages.map((imgUrl, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => openLightbox(idx)}
            className="group relative aspect-4/3 sm:aspect-16/10 w-full overflow-hidden rounded-2xl bg-slate-100 border border-slate-200/80 shadow-xs hover:shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
            aria-label={`Forstør billede ${idx + 1}`}
          >
            <img
              src={getResizedImageUrl(imgUrl, { width: 800, quality: 80 })}
              alt={`Transport4U galleri foto ${idx + 1}`}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {/* Subtle overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-900/80 px-2.5 py-1 rounded-lg backdrop-blur-xs">
                <i className="fa-solid fa-magnifying-glass-plus text-sky-400" aria-hidden="true" />
                Se i stor visning
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Fullscreen Lightbox */}
      <Lightbox
        images={gallery.images}
        currentIndex={activePhotoIdx}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setActivePhotoIdx}
      />
    </section>
  )
}
